import { useState, useEffect, useMemo } from "react";
import { Search, Package, Tag } from "lucide-react";
import { Shimmer, CardGridShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { getPlansWithVolume, type StockPlanWithVolume } from "@/utils/stocks-api";

function getStockBadgeVariant(count: number): "default" | "outline" | "destructive" {
  if (count >= 10) return "default";
  if (count >= 1) return "outline";
  return "destructive";
}

function getStockLabel(count: number): string {
  if (count >= 10) return "In Stock";
  if (count >= 1) return "Low Stock";
  return "Out of Stock";
}

export default function GiftCardsStocksPage() {
  const { token } = useAuth();

  const [plans, setPlans] = useState<StockPlanWithVolume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    getPlansWithVolume(token)
      .then(setPlans)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load stock data"))
      .finally(() => setLoading(false));
  }, [token]);

  const filtered = useMemo(() => {
    if (!search.trim()) return plans;
    const q = search.trim().toLowerCase();
    return plans.filter(
      (p) =>
        p.whatsub_plan.whatsub_service.service_name.toLowerCase().includes(q) ||
        p.whatsub_plan.plan_name.toLowerCase().includes(q)
    );
  }, [plans, search]);

  // Summary stats
  const totalCards = plans.reduce((s, p) => s + p.count, 0);
  const totalValue = plans.reduce((s, p) => s + p.totalValue, 0);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-1">
          Gift Cards &mdash; Stocks
        </h1>
        <p className="text-muted-foreground">
          Monitor available gift card inventory across all plans.
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary cards */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
          <Card>
            <CardContent className="p-5">
              <span className="text-sm text-muted-foreground font-medium">
                Total Plans
              </span>
              <h2 className="text-3xl font-bold mt-1">{plans.length}</h2>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <span className="text-sm text-muted-foreground font-medium">
                Total Cards Available
              </span>
              <h2 className="text-3xl font-bold mt-1">{totalCards.toLocaleString("en-IN")}</h2>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <span className="text-sm text-muted-foreground font-medium">
                Total Stock Value
              </span>
              <h2 className="text-3xl font-bold mt-1">
                {"\u20B9"}{totalValue.toLocaleString("en-IN")}
              </h2>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <span className="text-sm text-muted-foreground font-medium">
          {filtered.length} plan{filtered.length !== 1 ? "s" : ""}
        </span>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search plans..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-[240px]"
          />
        </div>
      </div>

      <Shimmer loading={loading} fallback={<CardGridShimmer />}>
        {filtered.length === 0 ? (
          <div className="flex justify-center py-20">
            <p className="text-muted-foreground">
              {search ? "No plans match your search" : "No stock data available"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((plan) => (
              <Card
                key={plan.plan_id}
                className="h-full transition-all duration-200 hover:border-primary hover:-translate-y-0.5 hover:shadow-lg"
              >
                <CardContent className="p-5 flex flex-col h-full">
                  {/* Header: Logo + Stock status */}
                  <div className="flex items-start justify-between mb-4">
                    <Avatar className="h-13 w-13 rounded-lg border">
                      <AvatarImage src={plan.whatsub_plan.whatsub_service.image_url} alt={plan.whatsub_plan.whatsub_service.service_name} />
                      <AvatarFallback className="rounded-lg">
                        {plan.whatsub_plan.whatsub_service.service_name[0]}
                      </AvatarFallback>
                    </Avatar>
                    <Badge variant={getStockBadgeVariant(plan.count)}>
                      {getStockLabel(plan.count)}
                    </Badge>
                  </div>

                  {/* Service + Plan name */}
                  <span className="text-sm font-bold mb-0.5">
                    {plan.whatsub_plan.whatsub_service.service_name}
                  </span>
                  <span className="text-xs text-muted-foreground mb-3">
                    {plan.whatsub_plan.plan_name}
                  </span>

                  <div className="flex-1" />

                  {/* Stats */}
                  <div className="flex flex-col gap-2 mt-2 pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Package className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Available</span>
                      </div>
                      <span className="text-sm font-bold">
                        {plan.count} cards
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Face Value</span>
                      </div>
                      <span className="text-xs font-semibold">
                        {"\u20B9"}{plan.value.toLocaleString("en-IN")} each
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Total Stock Value</span>
                      <span className="text-sm font-bold text-primary">
                        {"\u20B9"}{plan.totalValue.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </Shimmer>
    </div>
  );
}
